import { NavigateBefore } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LoadingButton from "@mui/lab/LoadingButton";
import {
	Button,
	Checkbox,
	FormControl,
	FormControlLabel,
	FormHelperText,
	FormLabel,
	MenuItem,
	TextField
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import { GridActionsCellItem } from "@mui/x-data-grid-premium";
import axios from 'axios'
import dayjs from "dayjs";
import { ErrorMessage, FormikProvider } from 'formik'
import { isEqual } from "lodash/lang";
import { enqueueSnackbar } from "notistack";
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import './Form.css'
import PageMenu from "../../../../components/custom/app/pageMenu/pageMenu";
import PageContentWrapper from "../../../../components/mui/app/pageContentWrapper/pageContentWrapper";
import PageTitle from "../../../../components/mui/app/pageTitle/pageTitle";
import CustomAutocomplete from "../../../../components/mui/form/autocomplete/autocomplete";
import Modal from "../../../../components/mui/general/modal/modal";
import EditableMUICellValidation
	from "../../../../components/mui/grids/editableMUICellValidation/editableMUICellValidation";
import EditableMUIDataGridAsField
	from "../../../../components/mui/grids/editableMUIDataGridAsField/editableMUIDataGridAsField";
import EditableMuiDataGridCustomFields
	from "../../../../components/mui/grids/editableMUIDataGridCustomFields/editableMUIDataGridCustomFields";
import { API } from "../../../../helpers/constants";
import { handleAxiosErrors } from "../../../../helpers/errorHandling";
import { muiGridLocales } from "../../../../helpers/muiGridLocales";
import {
	formControlDefaults,
	textFieldDefaults,
	textFieldSelectDefaults
} from "../../../../helpers/theme/defaultProps";
import useUserData from "../../../../hooks/user/useUserData";
import ValidatedDateEditCell from "../../../parameters/components/validatedDateEditCell";
import useUserForm from '../../hooks/useUserForm'
import { USERS } from "../../locales/namespaces";
import Upload from "../Upload/Upload";
import { useHistory } from "react-router-dom";

function Form(props) {
	const { token, dateFormat, language } = useUserData()
	const { id, role, user, staticData, onDelete, costs } = props
	const { t } = useTranslation(USERS)
	//μεταβλητή για να κρύβω τα στοιχεία σύνδεσης (όνομα χρήστη, κωδικός, επαλήθευση), όταν ο ρόλος είναι εργοδηγός ή εργάτης
	const showFields = role !== '1';
	const { formik, loadingSubmit } = useUserForm(({ id, user, staticData, costs, role, showFields }))
	const [skills, setSkills] = useState(staticData.skills)
	const [isUploaderVisible, setIsUploaderVisible] = useState(false)
	const [showPassword, setShowPassword] = useState(false)
	const [showConfirm, setShowConfirm] = useState(false)
	
	const handleSearch = (evt, name) => {
		let url
		let setter
		switch (name) {
			case 'userSkills':
				url = `${API}/en/user/skills`
				setter = setSkills
				break
			default:
				break
		}
		axios({
			method: 'post',
			data: { 'query': evt.target.value },
			url: url,
			headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
		}).then((response) => {
			if (response.data.data.length > 0) {
				setter(response.data.data)
			}
		}).catch((error) => {
			enqueueSnackbar(handleAxiosErrors(error, t('An error occurred!')), {
				variant: 'warning',
				autoHideDuration: 3000,
			})
		})
	}
	
	const handleUploadModal = (visible) => {
		setIsUploaderVisible(visible)
	}
	const handleImageFormState = (newValue) => {
		formik.setFieldValue('UserImage', newValue)
	}
	
	const newRowModel = {
		FromDt: dayjs(),
		ToDt: dayjs(),
		WorkCost: '',
		ExtraWorkCost: '',
	}
	
	const columns = useMemo(() => {
		
		const handleRowDelete = (id) => {
			const updatedRows = formik.values.costs?.filter((row) => row.id !== id)
			formik.setFieldValue('costs', updatedRows)
		}
		
		return [
			{
				field: 'actions',
				headerName: t('Actions'),
				width: 80,
				type: 'actions',
				getActions: (params) => [
					<GridActionsCellItem
						onClick={() => handleRowDelete(params.id)}
						icon={<DeleteIcon/>}
						label={t('Delete')}
						color={'error'}
					/>,
				],
			},
			{
				field: 'FromDt',
				headerName: t('From Date'),
				width: 90, maxWidth: 180, flex: 1,
				align: 'center',
				editable: true,
				type: 'date',
				valueFormatter: (value) => value ? dayjs(value).format(dateFormat) : null,
				preProcessEditCellProps: (params) => {
					const inputValue = params.props.value
					const hasValue = inputValue ? typeof inputValue !== 'string' ? dayjs(inputValue).format(dateFormat) : inputValue : null
					const validDate = inputValue ? typeof inputValue !== 'string' ? inputValue?.isValid() : true : false
					if (!hasValue) {
						return { ...params.props, error: t('Required') };
					} else if (!validDate) {
						return { ...params.props, error: t('Insert a valid date') };
					}
					return { ...params.props, error: null }; // No error
				},
				renderEditCell: (params) => {
					return (
						<EditableMUICellValidation {...params}>
							<Box>
								<EditableMuiDataGridCustomFields
									type={'datePicker'}
									params={params}
									name={'FromDt'}
									otherInputParams={{
										inputFormat: dateFormat,
										slotProps: {
											textField: {
												size: 'small',
												variant: 'standard'
											}
										},
										views: ['year', 'month', 'day']
									}}
								/>
							</Box>
						</EditableMUICellValidation>
					)
				},
			},
			{
				field: 'WorkCost',
				headerName: t('Work Cost'),
				width: 150, maxWidth: 300, flex: 1,
				align: 'right',
				editable: true,
				type: 'number',
				valueGetter: (value, row, column, apiRef) => value ? Number(value) : 0,
				preProcessEditCellProps: (params) => {
					const inputValue = params.props.value
					const inputMinLength = Number(params.props.value) < 0;
					if (!inputValue && inputValue !== 0) {
						return { ...params.props, error: t('Required') };
					} else if (inputMinLength) {
						return { ...params.props, error: t('Insert a positive number') };
					}
					return { ...params.props, error: null }; // No error
				},
				renderEditCell: (params) => {
					return (
						<EditableMUICellValidation {...params}>
							<Box>
								<EditableMuiDataGridCustomFields
									type={'number'}
									params={params}
									name={'WorkCost'}
								/>
							</Box>
						</EditableMUICellValidation>
					)
				},
			},
			{
				field: 'ExtraWorkCost',
				headerName: t('Extra Work Cost'),
				width: 90, maxWidth: 180, flex: 1,
				align: 'right',
				editable: true,
				type: 'number',
				valueGetter: (value, row, column, apiRef) => value ? Number(value) : 0,
				preProcessEditCellProps: (params) => {
					const inputValue = params.props.value
					const inputMinLength = Number(params.props.value) < 0;
					if (!inputValue && inputValue !== 0) {
						return { ...params.props, error: t('Required') };
					} else if (inputMinLength) {
						return { ...params.props, error: t('Insert a positive number') };
					}
					return { ...params.props, error: null }; // No error
				},
				renderEditCell: (params) => {
					return (
						<EditableMUICellValidation {...params}>
							<Box>
								<EditableMuiDataGridCustomFields
									type={'number'}
									params={params}
									name={'ExtraWorkCost'}
								/>
							</Box>
						</EditableMUICellValidation>
					)
				},
			},
		
		]
	}, [formik, t, dateFormat])
	
	//Εμφάνιση μηνύματος κατα το κλικ "επιστροφής", εαν έχει προηγηθεί αλλαγή στη φόρμα.
	const history = useHistory();
	const handleReturnClick = (e) => {
		const hasChanges = !isEqual(formik.initialValues, formik.values);
		if (hasChanges) {
			const confirmLeave = window.confirm("Έχετε πραγματοποιήσει αλλαγές...");
			if (!confirmLeave) return;
		}
		history.push(`/users/${role}`);
	};
	
	//Ειδοποίηση κατά το refresh της σελίδας με μήνυμα default από το browser, όπου δεν είναι δυνατή η αλλαγή του σε κάτι custom, όταν έχει προηγηθεί αλλαγή στη φόρμα.
	useEffect(() => {
		const handleBeforeUnload = (e) => {
			if (formik.dirty) {
				e.preventDefault();
				e.returnValue = '';
			}
		};
		
		window.addEventListener('beforeunload', handleBeforeUnload);
		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
		};
	}, [formik.dirty]);
	
	return (
		<FormikProvider value={formik}>
			<form
				autoComplete="off"
				id="userForm"
				encType="multipart/form-data"
				style={{ marginTop: '10px' }}
			>
				<Box display={'flex'}
					 flexWrap={'wrap'}
					 justifyContent={'space-between'}
					 gap={1}>
					<PageMenu>
						<Button
							startIcon={<NavigateBefore/>}
							onClick={handleReturnClick}
						>
							{t('Return')}
						</Button>
						<LoadingButton
							loading={loadingSubmit}
							onClick={formik.submitForm}
							variant="contained"
							startIcon={<SaveIcon/>}
						>
							{t('Save')}
						</LoadingButton>
					</PageMenu>
					{id && (
						// <PermissionHOC requiredPermissions={['view_all_users']}>
						<Button
							size={'small'}
							variant={'contained'}
							color={'error'}
							onClick={onDelete}
						>
							{t('Delete')}
						</Button>
						// </PermissionHOC>
					)}
				</Box>
				<PageContentWrapper>
					<PageTitle
						withDivider
						title={
							!id && role === '0' ? t('User Create') :
								id && role === '0' ? t('User Edit') :
									!id && role === '1' ? t('Worker Create') :
										id && role === '1' ? t('Worker Edit') :
											''
						}
					/>
					<Grid
						container
						columnSpacing={3}
						sx={{ marginLeft: '0px' }}
					>
						<Grid size={{ xs: 12, sm: 6, md: 6 }}>
							<Box>
								<Grid container spacing={2}>
									<Grid size={{ md: 10 }}>
										<FormControl
											{...formControlDefaults}
										>
											<FormLabel required>{t('Name')}</FormLabel>
											<TextField
												{...textFieldDefaults}
												name={'LoginName'}
												value={formik.values.LoginName}
												onChange={formik.handleChange}
												onBlur={formik.handleBlur}
											/>
											<FormHelperText>
												<ErrorMessage name={'LoginName'}/>
											</FormHelperText>
										</FormControl>
									
									</Grid>
									<Grid size={{ md: 2 }} sx={{ paddingLeft: '3%', marginTop: '25px' }}>
										<FormControl
											{...formControlDefaults}
										>
											<FormControlLabel
												control={
													<Checkbox
														checked={formik.values.isActive}
														onChange={formik.handleChange}
														name="isActive"
														onBlur={formik.handleBlur}
													/>
												}
												label={<FormLabel>{t('Active')}</FormLabel>}
											/>
										</FormControl>
									
									</Grid>
								</Grid>
							</Box>
							<FormControl
								{...formControlDefaults}
							>
								<FormLabel>{t('Father Name')}</FormLabel>
								<TextField
									{...textFieldDefaults}
									name={'FatherName'}
									value={formik.values.FatherName}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
								/>
								<FormHelperText>
									<ErrorMessage name={'FatherName'}/>
								</FormHelperText>
							</FormControl>
							{!showFields &&
								<FormControl
									{...formControlDefaults}
								>
									<FormLabel>{t('Code')}</FormLabel>
									<TextField
										{...textFieldDefaults}
										name={'Code'}
										value={formik.values.Code}
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
									/>
								</FormControl>
							}
							<FormControl
								{...formControlDefaults}
							>
								<FormLabel>{t('Skills')}</FormLabel>
								<CustomAutocomplete
									identifier="id"
									value={formik.values.userSkills}
									onChange={(evt, item) => {
										formik.setFieldValue('userSkills', item)
									}}
									options={skills}
									labelKey={'Description'}
									onInputChange={(event) => handleSearch(event, 'userSkills')}
									otherprops={{
										multiple: true,
										disableCloseOnSelect: true
									}}
								/>
							</FormControl>
							{showFields && (
								<FormControl
									{...formControlDefaults}
								>
									<FormLabel required>{t('Role')}</FormLabel>
									<TextField
										{...textFieldSelectDefaults}
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
										value={formik.values.RoleId}
										name={'RoleId'}
									>
										{staticData.roles?.map((role) => (
											<MenuItem
												key={role.id}
												value={role.id}
											>
												{role.Description}
											</MenuItem>
										))}
									</TextField>
									<FormHelperText>
										<ErrorMessage name={'RoleId'}/>
									</FormHelperText>
								</FormControl>
							)}
							{showFields && (
								<FormControl
									{...formControlDefaults}
								>
									<FormLabel required>{t('Username')}</FormLabel>
									<TextField
										{...textFieldDefaults}
										name={'username'}
										value={formik.values.username}
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
									/>
									<FormHelperText>
										<ErrorMessage name={'username'}/>
									</FormHelperText>
								</FormControl>
							)}
							{showFields && (
								<FormControl
									{...formControlDefaults}
								>
									<FormLabel required={!id && true}>{t('Password')}</FormLabel>
									<TextField
										{...textFieldDefaults}
										name={'password'}
										type={showPassword ? "text" : "password"}
										value={formik.values.password}
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
										slotProps={{ //
											input: {// <-- This is where the toggle button is added.
												autoComplete: 'new-password',
												form: {
													autoComplete: 'off',
												},
												endAdornment: (
													<InputAdornment position="end">
														<IconButton
															aria-label="toggle password visibility"
															onClick={() => setShowPassword((prev) => !prev)}
														>
															{showPassword ? <VisibilityIcon/> :
																<VisibilityOffIcon/>}
														</IconButton>
													</InputAdornment>
												)
											}
										}}
									/>
									<FormHelperText>
										<ErrorMessage name={'password'}/>
									</FormHelperText>
								</FormControl>
							)}
							{showFields && (
								<FormControl
									{...formControlDefaults}
								>
									<FormLabel required={!id && true}>{t('Confirm Password')}</FormLabel>
									<TextField
										{...textFieldDefaults}
										name={'passwordConfirmation'}
										type={showConfirm ? "text" : "password"}
										value={formik.values.passwordConfirmation}
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
										slotProps={{
											input: {// <-- This is where the toggle button is added.
												autoComplete: 'new-password',
												form: {
													autoComplete: 'off',
												},
												endAdornment: (
													<InputAdornment position="end">
														<IconButton
															aria-label="toggle password visibility"
															onClick={() => setShowConfirm((prev) => !prev)}
														>
															{showConfirm ? <VisibilityIcon/> :
																<VisibilityOffIcon/>}
														</IconButton>
													</InputAdornment>
												)
											}
										}}
									/>
									<FormHelperText>
										<ErrorMessage name={'passwordConfirmation'}/>
									</FormHelperText>
								</FormControl>
							)}
							<FormControl
								{...formControlDefaults}
							>
								<FormLabel>{t('Email')}</FormLabel>
								<TextField
									{...textFieldDefaults}
									name={'email'}
									value={formik.values.email}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
								/>
								<FormHelperText>
									<ErrorMessage name={'email'}/>
								</FormHelperText>
							</FormControl>
						</Grid>
						{role === '1' &&
							<Grid size={{ xs: 12, sm: 6, md: 6 }}>
								<FormControl
									{...formControlDefaults}
								>
									<EditableMUIDataGridAsField
										formik={formik}
										newRowModel={newRowModel}
										fieldName={'costs'}
										columns={columns}
										rowIdentifier={'id'}
										dataGridOtherProps={{
											localeText: muiGridLocales(language)
										}}
									/>
									<FormHelperText>
										<ErrorMessage name={'costs'}/>
									</FormHelperText>
								</FormControl>
							</Grid>
						}
					</Grid>
				</PageContentWrapper>
			
			</form>
			<Modal show={isUploaderVisible}>
				<Upload
					handleUploadModal={handleUploadModal}
					handleImageFormState={handleImageFormState}
					urlParameter="user"
				/>
			</Modal>
		</FormikProvider>
	)
}

export default Form